import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { PencilIcon, TrashIcon, ArrowPathIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { PageHeader, Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Badge from '@/components/ui/Badge';
import Dropdown from '@/components/ui/Dropdown';
import EmptyState from '@/components/ui/EmptyState';
import Pagination from '@/components/ui/Pagination';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { BulkDeleteBar, SelectAllHeader, SelectRowCell } from '@/components/ui/BulkDelete';
import { useRowSelection } from '@/lib/useRowSelection';
import { useDebounce } from '@/lib/useDebounce';
import { toaster } from '@/components/ui/toaster';
import { queryKeys } from '@/lib/queryKeys';
import {
  deleteCycle as deleteCycleById,
  getCycle,
  getCycles,
  getCycleSessions,
  type CycleDetail,
  type CycleListItem,
  type CycleSessionsResponse,
} from '@/api/cyclesService';
import { useFormats } from '@/features/reference/useReference';
import { useCycles } from './useCycles';
import { duplicateIdentifier, duplicateName, sessionsToInputs } from './cycleDuplicate';
import CycleModal from './CycleModal';

const FILTERS: { value: string; label: string }[] = [
  { value: 'all', label: 'Tous' },
  { value: 'used', label: 'Utilisés' },
  { value: 'drafts', label: 'Brouillons' },
];

const rowId = (c: CycleListItem) => c.id;

const CyclesPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [dup, setDup] = useState<{ initial: CycleDetail; weeks: CycleSessionsResponse['weeks'] } | null>(null);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filter]);

  const filters = useMemo(
    () => ({ search: debouncedSearch || undefined, filter, page }),
    [debouncedSearch, filter, page]
  );

  const { items, total, pageSize, kpis, isLoading, deleteCycle, createCycle, createCycleWithSessions, isSaving, invalidate } =
    useCycles(filters);
  const { data: formats = [] } = useFormats();
  const selection = useRowSelection(items, rowId, filters);

  const onBulkDeleted = () => {
    invalidate();
    queryClient.invalidateQueries({ queryKey: queryKeys.pv2CycleKpis });
  };

  const openCreate = () => setModalOpen(true);
  const openDetail = (id: string) => navigate(`/cycles/${id}`);

  const closeModal = () => {
    setModalOpen(false);
    setDup(null);
  };

  const prepareDuplicate = async (cycle: CycleListItem) => {
    try {
      const [detail, sessions, siblings] = await Promise.all([
        getCycle(cycle.id),
        getCycleSessions(cycle.id),
        getCycles({ search: cycle.identifier, pageSize: 100 }),
      ]);
      setDup({
        initial: {
          ...detail,
          name: duplicateName(detail.name),
          identifier: duplicateIdentifier(detail.identifier, siblings.items.map((i) => i.identifier)),
          usedInCount: 0,
        },
        weeks: sessions.weeks,
      });
    } catch {
      toaster.create({ title: 'Duplication impossible', description: 'Lecture du cycle source échouée.', type: 'error' });
    }
  };

  const handleDelete = (cycle: CycleListItem) => {
    if (window.confirm(`Supprimer le cycle « ${cycle.name} » ?\nCette action est irréversible.`)) {
      deleteCycle(cycle.id);
    }
  };

  return (
    <>
      <PageHeader
        title="Cycles"
        description="Entrainement / Cycles"
        action={<Button onClick={openCreate}>+ Nouveau cycle</Button>}
      />

      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <p className="text-sm text-gray-500 dark:text-gray-400">Cycles</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{kpis?.total ?? '—'}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500 dark:text-gray-400">Utilisés dans un plan</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{kpis?.usedInPlans ?? '—'}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500 dark:text-gray-400">Brouillons</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{kpis?.drafts ?? '—'}</p>
        </Card>
      </div>

      <Card className="mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-72">
            <Input
              label="Rechercher"
              placeholder="Nom ou identifiant"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="inline-flex rounded-lg border border-gray-300 p-0.5 dark:border-gray-700">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={clsx(
                  'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  filter === f.value
                    ? 'bg-brand-500 text-white'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {isLoading && <p className="text-sm text-gray-500">Chargement…</p>}

      {!isLoading && items.length === 0 && (
        <EmptyState
          icon={<ArrowPathIcon />}
          title="Aucun cycle"
          description="Créez un cycle pour définir sa longueur et sa matrice de priorités."
          action={<Button onClick={openCreate}>+ Nouveau cycle</Button>}
        />
      )}

      {!isLoading && items.length > 0 && (
        <Card className="p-0">
          <BulkDeleteBar
            selection={selection}
            labelOf={(c) => c.name}
            remove={(c) => deleteCycleById(c.id)}
            onDone={onBulkDeleted}
          />
          <Table>
            <THead>
              <tr>
                <SelectAllHeader selection={selection} />
                <TH>Cycle</TH>
                <TH>Nom affiché</TH>
                <TH>Semaines</TH>
                <TH>Priorité par format</TH>
                <TH>Utilisé dans</TH>
                <TH className="text-right">Actions</TH>
              </tr>
            </THead>
            <TBody>
              {items.map((cycle) => (
                <TR key={cycle.id}>
                  <SelectRowCell selection={selection} row={cycle} />
                  <TD className="font-bold text-gray-900 dark:text-gray-100">
                    <button
                      type="button"
                      onClick={() => openDetail(cycle.id)}
                      className="inline-flex items-center gap-2 text-left hover:text-brand-600 dark:hover:text-brand-300"
                    >
                      {cycle.name}
                      {cycle.isDraft && (
                        <Badge color="yellow" className="font-medium">
                          Brouillon
                        </Badge>
                      )}
                      {cycle.enableScaling && (
                        <Badge color="green" className="font-medium">
                          Scaling
                        </Badge>
                      )}
                    </button>
                    <span className="block text-xs font-normal text-gray-400">{cycle.identifier}</span>
                  </TD>
                  <TD className={cycle.displayName ? undefined : 'text-gray-400'}>{cycle.displayName ?? '—'}</TD>
                  <TD className="text-gray-500">
                    {cycle.isDraft ? '—' : `${cycle.minWeekNumber} – ${cycle.maxWeekNumber}`}
                  </TD>
                  <TD className="text-gray-500">
                    <span className="flex flex-wrap gap-1.5">
                      {formats
                        .filter((f) => (cycle.priorityByFormat[f.identifier] ?? 0) > 0)
                        .map((f) => (
                          <Badge key={f.id} color="blue" className="font-semibold">
                            {f.slug} {cycle.priorityByFormat[f.identifier]}
                          </Badge>
                        ))}
                    </span>
                  </TD>
                  <TD className="text-gray-500">
                    {cycle.usedInCount > 0 ? `${cycle.usedInCount} plan(s)` : '—'}
                  </TD>
                  <TD className="text-right">
                    <Dropdown
                      actions={[
                        {
                          label: 'Ouvrir',
                          icon: <PencilIcon className="h-4 w-4" />,
                          onClick: () => openDetail(cycle.id),
                        },
                        {
                          label: 'Dupliquer',
                          icon: <DocumentDuplicateIcon className="h-4 w-4" />,
                          onClick: () => void prepareDuplicate(cycle),
                        },
                        {
                          label: 'Supprimer',
                          icon: <TrashIcon className="h-4 w-4" />,
                          destructive: true,
                          onClick: () => handleDelete(cycle),
                        },
                      ]}
                    />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
        </Card>
      )}

      <CycleModal
        open={modalOpen || Boolean(dup)}
        onClose={closeModal}
        initial={dup?.initial ?? null}
        resetToken={dup ?? modalOpen}
        title={dup ? 'Dupliquer le cycle' : 'Nouveau cycle'}
        submitLabel={dup ? 'Créer la copie' : 'Créer'}
        onSubmit={
          dup
            ? (payload) =>
                createCycleWithSessions({
                  payload,
                  sessions: sessionsToInputs(dup.weeks, payload.maxWeekNumber),
                })
            : createCycle
        }
        onCreated={(cycle) => navigate(`/cycles/${cycle.id}`)}
        isSaving={isSaving}
      />
    </>
  );
};

export default CyclesPage;
